import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Pressable,
  Image,
  ImageBackground,
  Dimensions,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

interface InternalUserAccessProps {
  navigate: (path: string) => void;
}

export const InternalUserAccess: React.FC<InternalUserAccessProps> = ({ navigate }) => {
  const [screenData, setScreenData] = React.useState(Dimensions.get('window'));
  const isWeb = Platform.OS === 'web';
  const isLargeScreen = isWeb && screenData.width >= 1024;

  React.useEffect(() => {
    if (isWeb) {
      const subscription = Dimensions.addEventListener('change', ({ window }) => {
        setScreenData(window);
      });
      
      return () => subscription?.remove();
    }
  }, [isWeb]);

  return (
    <SafeAreaView style={styles.container}>
      <View style={[
        styles.content, 
        isWeb && { height: screenData.height },
        isLargeScreen ? styles.horizontalLayout : styles.verticalLayout
      ]}>
        <View style={[
          styles.imageSection,
          isLargeScreen ? styles.imageSectionHorizontal : styles.imageSectionVertical
        ]}>
          <ImageBackground
            source={require('../assets/images/datacenter-background.png')}
            style={styles.backgroundImage}
            resizeMode="cover"
          >
            <View style={styles.overlay} />
          </ImageBackground>
        </View>
        {isLargeScreen ? (
          <View style={styles.contentSectionHorizontal}>
            <View style={styles.contentCard}>
              {/* Logo */}
              <View style={styles.logoContainer}>
                <Image 
                  source={require('../assets/vertiv-logo-black.png')}
                  style={styles.logo as any}
                  resizeMode="contain"
                />
              </View>

              {/* Title */}
              <Text style={styles.title}>Application Access Request</Text>

              {/* Primary Message */}
              <Text style={styles.primaryMessage}>
                You need permission to access this application.
              </Text>

              {/* Secondary Message */}
              <Text style={styles.secondaryMessage}>
                You currently don't have permission to view this site. If you believe you should have access, 
                please click below to request it. Our team will review your request and get back to you shortly.
              </Text>

              {/* Request Access Button */}
              <Pressable style={styles.requestButton} onPress={() => navigate('/access-request-success')}>
                <Text style={styles.requestButtonText}>Request Access</Text>
              </Pressable>
            </View>
          </View>
        ) : (
          <View style={styles.contentSectionVertical}>
            <View style={styles.contentCardVertical}>
              {/* Logo */}
              <View style={styles.logoContainer}>
                <Image 
                  source={require('../assets/vertiv-logo-black.png')}
                  style={styles.logo as any}
                  resizeMode="contain"
                />
              </View>

              {/* Title */}
              <Text style={styles.title}>Application Access Request</Text>

              {/* Primary Message */}
              <Text style={styles.primaryMessage}>
                You need permission to access this application.
              </Text>

              {/* Secondary Message */}
              <Text style={styles.secondaryMessage}>
                You currently don't have permission to view this site. If you believe you should have access, 
                please click below to request it. Our team will review your request and get back to you shortly.
              </Text>

              {/* Request Access Button */}
              <Pressable style={styles.requestButton} onPress={() => navigate('/access-request-success')}>
                <Text style={styles.requestButtonText}>Request Access</Text>
              </Pressable>
            </View>
          </View>
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
  },
  content: {
    flex: 1,
    overflow: 'hidden',
  },
  horizontalLayout: {
    flexDirection: 'row',
  },
  verticalLayout: {
    flexDirection: 'column',
    ...Platform.select({
      web: {
        position: 'relative',
      },
    }),
  },
  imageSection: {
    backgroundColor: '#f0f0f0',
  },
  imageSectionHorizontal: {
    flex: 1,
  },
  imageSectionVertical: {
    height: 300,
    ...Platform.select({
      web: {
        height: '40vh' as any,
        minHeight: 250,
      },
    }),
  },
  backgroundImage: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
  contentSectionHorizontal: {
    flex: 1,
    paddingHorizontal: 60,
    paddingVertical: 40,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentSectionVertical: {
    paddingHorizontal: 24,
    zIndex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    ...Platform.select({
      ios: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        paddingVertical: 40,
      },
      android: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        paddingVertical: 30,
      },
      web: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        paddingHorizontal: 32,
        paddingVertical: 40,
      },
    }),
  },
  contentCard: {
    width: '100%',
    maxWidth: 440,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    backgroundColor: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
  },
  contentCardVertical: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 32,
    marginHorizontal: 0,
    maxWidth: 400,
    width: '100%',
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      ios: {
        paddingVertical: 40,
        shadowColor: '#000',
        shadowOffset: {
          width: 0,
          height: 4,
        },
        shadowOpacity: 0.1,
        shadowRadius: 12,
      },
      android: {
        paddingVertical: 36,
        elevation: 8,
      },
      web: {
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
        paddingHorizontal: 32,
        paddingVertical: 40,
      },
    }),
  },
  logoContainer: {
    marginBottom: 24,
    alignItems: 'center',
  },
  logo: {
    width: 80,
    height: 80,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 16,
    color: '#1a1a1a',
    lineHeight: 32,
  },
  primaryMessage: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 12,
    color: '#1a1a1a',
    lineHeight: 24,
  },
  secondaryMessage: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 32,
  },
  requestButton: {
    backgroundColor: '#FF6B35',
    borderRadius: 6,
    paddingVertical: 14,
    paddingHorizontal: 32,
    alignItems: 'center',
    width: '100%',
    ...Platform.select({
      web: {
        boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)',
      },
      android: {
        elevation: 4,
      },
      ios: {
        shadowColor: '#FF6B35',
        shadowOffset: {
          width: 0,
          height: 2,
        },
        shadowOpacity: 0.3,
        shadowRadius: 4,
      },
    }),
  },
  requestButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});
